import React, { useMemo } from 'react';
import { Disclosure } from '@headlessui/react';
import { getNextSteps } from 'utils/ShipmentWebAPIUtils';
import { useTranslation } from 'react-i18next';
import SessionStore from 'stores/SessionStore';
import AppointmentWindowNextSteps from 'components/modules/shipments/next-steps/AppointmentWindowNextStep';
import AssignCarrierNextStep from 'components/modules/shipments/next-steps/AssignCarrierNextStep';
import StopCompleteNextStep from 'components/modules/shipments/next-steps/CompleteNextStep';
import DateNextSteps from 'components/modules/shipments/next-steps/DateNextSteps';
import ReferenceNumbersNextStep from 'components/modules/shipments/next-steps/ReferenceNumberNextStep';
import StopArrivalNextStep from 'components/modules/shipments/next-steps/StopArrivalNextSteps';
import NonActionableNextStep from 'components/modules/shipments/next-steps/NonActionableNextStep';
import AddAccessorialNextStep from 'components/modules/shipments/next-steps/AddAccessorialNextStep';
import ApproveAccessorialNextStep from 'components/modules/shipments/next-steps/ApproveAccessorialNextStep';
import { useQuery } from '@tanstack/react-query';

const POLL = 30000;

const STEPS = {
  DATE: 'date',
  ASSIGN_CARRIER: 'assign_carrier',
  APPOINTMENT_WINDOW: 'appointment_window',
  COMPLETE: 'complete',
  REFERENCE_NUMBERS: 'reference_numbers',
  ARRIVAL: 'arrival',
  ADD_ACCESSORIAL: 'add_accessorial',
  APPROVE_ACCESSORIAL: 'approve_accessorial',
  NON_ACTIONABLE: 'non_actionable',
};

function filterNextStepsByUserType(mapped, isCarrier) {
  if (isCarrier) {
    return mapped.filter((step) => {
      return !(
        Array.isArray(step?.meta?.userType) &&
        step?.meta?.userType.includes('admin')
      );
    });
  }
  return mapped;
}

export default function NextSteps({ shipment }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['next-actions', shipment._id],
    queryFn: async () => {
      const {
        entity: {
          results: { upcoming, complete },
        },
      } = await getNextSteps(shipment._id);
      return { upcoming, complete };
    },
    refetchInterval: POLL,
  });

  const mapAdditionalStepAttributes = (steps) => {
    return (steps || []).map((step, stepIndex) => ({
      ...step,
      stepIndex,
      disabled: shouldDisableStopCompletion(steps, { ...step, stepIndex }),
    }));
  };

  const upcoming = data?.upcoming ?? [];
  const complete = data?.complete ?? [];
  const isCarrier = SessionStore.isCarrier;
  const nextSteps = useMemo(() => {
    const mapped = mapAdditionalStepAttributes(upcoming);
    return filterNextStepsByUserType(mapped, isCarrier);
  }, [upcoming, isCarrier]);

  const completedSteps = useMemo(() => {
    const mapped = mapAdditionalStepAttributes(complete);
    return filterNextStepsByUserType(mapped, isCarrier);
  }, [complete, isCarrier]);

  const renderViewMore = (open) => {
    const text = open ? 'HIDE' : 'VIEW_ALL';
    return (
      <span data-testid="span-156de9d6" className="text-xs text-gray-700">
        {t(text)}
      </span>
    );
  };

  const renderStep = (step) => {
    const isAdmin = SessionStore.isAdmin();
    switch (step.type) {
      case STEPS.DATE:
        return <DateNextSteps item={step} shipment={shipment} />;
      case STEPS.ASSIGN_CARRIER:
        return <AssignCarrierNextStep item={step} shipment={shipment} />;
      case STEPS.APPOINTMENT_WINDOW:
        return <AppointmentWindowNextSteps item={step} shipment={shipment} />;
      case STEPS.COMPLETE:
        return (
          <StopCompleteNextStep
            item={step}
            stops={shipment.stops}
            shipment={shipment}
          />
        );
      case STEPS.REFERENCE_NUMBERS:
        return (
          isAdmin && (
            <ReferenceNumbersNextStep item={step} shipment={shipment} />
          )
        );
      case STEPS.ARRIVAL:
        return <StopArrivalNextStep item={step} shipment={shipment} />;
      case STEPS.ADD_ACCESSORIAL:
        return (
          isAdmin && <AddAccessorialNextStep item={step} shipment={shipment} />
        );
      case STEPS.APPROVE_ACCESSORIAL:
        return (
          isAdmin && (
            <ApproveAccessorialNextStep item={step} shipment={shipment} />
          )
        );
      case STEPS.NON_ACTIONABLE:
        return <NonActionableNextStep item={step} />;
      default:
        return null;
    }
  };

  function shouldDisableStopCompletion(nextSteps, currentStep) {
    // Prevent completion if:
    // --there are unconfirmed appointments (except empty)
    // --there are arrivals required
    // --prior stops haven't been completed

    const { type, field, sequence, complete } = currentStep;

    if (complete) {
      return true;
    }

    if (
      !(
        ['arrival', 'complete'].includes(type) ||
        (type === 'appointment_window' && field === 'appointment_unconfirmed')
      )
    ) {
      return false;
    }

    const relevantSteps = nextSteps.filter(
      (step) => step?.stepIndex !== currentStep?.stepIndex,
    );

    if (!relevantSteps.length) {
      return false;
    }

    // Unconfirmed Appointments
    const unconfirmedAppointments = relevantSteps.filter(
      (step) =>
        step.type === 'appointment_window' &&
        step.field === 'appointment_unconfirmed' &&
        step.sequence < sequence,
    );

    // Stop Arrivals
    const incompletePriorArrivals = relevantSteps.filter(
      (step) => step.type === 'arrival' && step.sequence < sequence,
    );

    // Prior Stop Completions
    const incompletePriorStopCompletions = relevantSteps.filter(
      (step) => step.type === 'complete' && step.sequence < sequence,
    );

    const relevantStop = shipment?.stops.find(
      (s) => s?._id === currentStep?.meta?.stop,
    );
    if (relevantStop.empty) {
      return !!(
        incompletePriorStopCompletions.length || incompletePriorArrivals.length
      );
    }

    return !!(
      unconfirmedAppointments.length ||
      incompletePriorArrivals.length ||
      incompletePriorStopCompletions.length
    );
  }

  const renderCompletedActions = () => {
    if (completedSteps.length === 0) {
      return null;
    }
    return (
      <div data-testid="div-8abfee1c">
        <p
          data-testid="p-d84627f3"
          className="text-sm mb-2 text-gray-700 font-medium"
        >
          {t('COMPLETED_ACTIONS')}
        </p>
        <div data-testid="div-cb4b48b1">{completedSteps.map(renderStep)}</div>
      </div>
    );
  };

  const renderUpcomingActions = () => {
    return (
      <div data-testid="div-b089e1d3">
        <p
          data-testid="p-cfc1f0c1"
          className="text-sm mb-2 text-gray-700 font-medium"
        >
          {t('UPCOMING_ACTIONS')}
        </p>
        {nextSteps.map(renderStep)}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <div data-testid="div-5f0f4a46" className="space-y-4">
        {renderUpcomingActions()}
        <hr data-testid="hr-2058ba4c" className="" />
        {renderCompletedActions()}
      </div>
    );
  };

  const renderNextActions = (open) => {
    if (open) {
      return null;
    }
    return (
      <div data-testid="div-02289f44">
        {nextSteps.slice(0, 3).map(renderStep)}
      </div>
    );
  };

  return (
    <Disclosure>
      {({ open }) => (
        <div data-testid="div-53844f82" className="lg:col-span-4 mb-4">
          <h3 data-testid="h3-1cc5f47d" className="my-0 mb-4">
            {t('NEXT_STEPS')}
          </h3>
          {renderNextActions(open)}
          {!open && (
            <div data-testid="div-f8cd9d99" className="flex justify-end mt-2">
              <Disclosure.Button data-testid="undefined-d201503d">
                {renderViewMore(open)}
              </Disclosure.Button>
            </div>
          )}
          <Disclosure.Panel data-testid="undefined-8ca08c3b">
            {renderContent()}
          </Disclosure.Panel>
          {open && (
            <div data-testid="div-e7bbbecb" className="flex justify-end mt-2">
              <Disclosure.Button data-testid="undefined-6ee47da3">
                {renderViewMore(open)}
              </Disclosure.Button>
            </div>
          )}
        </div>
      )}
    </Disclosure>
  );
}
